// © Microsoft Corporation. All rights reserved.

import {
    AccentButton,
    Heading,
    HeadingSize,
    HeadingTag,
    StealthButton
} from "@microsoft/fast-components-react-msft";
import {
    DialogProps,
    Dialog as MSFTBaseDialog
} from "@microsoft/fast-components-react-base";
import { GlyphInline, Icon } from "@msnews/icon" ;
import { ShareDialogHandledProps, ShareDialogProps, ShareDialogStrings } from "./ShareDialog.props";
import {
    SocialMedia,
    SocialMediaConfig,
    SocialMediaDefaultConfig
} from "@msnews/views-helpers";
import { baseDialogStyles, setStyleByZIndex } from "./ShareDialog.styles";

import Foundation from "@microsoft/fast-components-foundation-react";
import { HandledPropsKeys } from "@msnews/core";
import React from "react";
import { SocialMediaIcon } from "@msnews/social-media-icon";
import { StringResource } from "./StringResource";
import { TelemetryTags } from "@msnews/telemetry-contracts";
import { logger } from "@msnews/diagnostics";
import manageJss from "@microsoft/fast-jss-manager-react";

/** Base MSFT dialog with overriden style */
const Dialog = manageJss(baseDialogStyles)(MSFTBaseDialog);

/** Duration the button should present the "link copied" text after a user click */
const linkCopiedDisplayDuration = 1600;

/**
 * Share dialog state interface
 * @export
 * @interface ShareDialogState
 */
export interface ShareDialogState {
    isLinkCopied: boolean
}
/**
 * ShareDialog Component
 * @class - ShareDialog
 * @classdesc - The ShareDialog component. Renders the dialog that provides social media sharing options
 * @default
 */
export class ShareDialog extends Foundation<ShareDialogHandledProps, ShareDialogHandledProps, ShareDialogState> {
    /**
     * Component HandledProps enumeration. Anything not enumerated here will be included in the experience root HTML tag via unhandledProps()
     */
    protected handledProps: HandledPropsKeys<ShareDialogHandledProps> = {
        baseDialogProps: undefined,
        closeButtonTelemetryTags: undefined,
        closeButtonTelemetryObject: undefined,
        contentTitle: undefined,
        contentUrl: undefined,
        emailBody: undefined,
        copyButtonAttributes: undefined,
        copyButtonTelemetryObject: undefined,
        copyButtonTelemetryTags: undefined,
        copyLinkOcid: undefined,
        dialogStrings: undefined,
        enabled: undefined,
        managedClasses: undefined,
        onClose: undefined,
        renderOneColLayout: undefined,
        socialMediaConfigOverride: undefined,
        socialMediaItems: undefined,
        shareIconTelemetryObject: undefined,
        shareIconTelemetryTags: undefined,
        baseDialogStyleOverride: undefined,
        zIndex: undefined,
        requestContext: undefined
    };

    /**
     * Stores the strings to be used in the dialog. Merged default strings with passed from props
     * @type {ShareDialogStrings}
     */
    private dialogStrings: ShareDialogStrings;

    /**
     * Social media items config that stores merged values from default and props
     * @type {SocialMediaConfig}
     */
    private socialMediaItemsConfig: SocialMediaConfig;

    /**
     * Ref to input field with shared url
     * @type {React.RefObject<HTMLInputElement>}
     */
    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    /**
     * Constructs a ShareDialog component
     * @constructor
     * @param { ShareDialogProps } props props contract
     */
    constructor(props: ShareDialogProps) {
        super(props);
        this.state = {
            isLinkCopied: false
        };

        // Merges some values from props with default values
        this.dialogStrings = { ...StringResource.dialogStrings, ...props.dialogStrings };

        this.setSocialMediaItemsConfig();
    }

    /**
     * Component renderer
     * @public
     * @method
     * @returns {JSX.Element} the ShareDialog component
     */
    public render(): JSX.Element {
        const { managedClasses, enabled, contentUrl, baseDialogProps, onClose, children, closeButtonTelemetryTags, baseDialogStyleOverride, zIndex } = this.props;
        if (!enabled || !contentUrl) {
            return null;
        }

        return (
            <Dialog
                {...this.coerceDialogProps(baseDialogProps)}
                {...this.unhandledProps()}
                onDismiss={onClose}
                jssStyleSheet={setStyleByZIndex(baseDialogStyleOverride, zIndex)}>
                <div className={managedClasses.dialogContainer}>
                    <div className={managedClasses.header}>
                        <Heading size={HeadingSize._7} tag={HeadingTag.h3} className={managedClasses.headerText}>
                            {this.dialogStrings.dialogTitle}
                        </Heading>
                        <StealthButton
                            onClick={onClose}
                            className={managedClasses.closeButton}
                            title={this.dialogStrings.dialogButtonClose}
                            {...closeButtonTelemetryTags}>
                            <Icon className={managedClasses.closeIcon} glyphType={GlyphInline.Close} />
                        </StealthButton>
                    </div>
                    {children}
                    {this.renderSocialIconsList()}
                    {this.renderCopyLink()}
                </div>
            </Dialog>
        );
    }

    /**
     * Renders the copy link input and button part of the share dialog
     * @private
     * @method
     * @returns {JSX.Element} the content part of the ShareDialog component
     */
    private renderCopyLink(): JSX.Element {
        const { managedClasses, copyLinkOcid, contentUrl, copyButtonAttributes, copyButtonTelemetryObject, copyButtonTelemetryTags } = this.props;

        const url = copyLinkOcid ? `${contentUrl}?ocid=${copyLinkOcid}` : contentUrl;
        const buttonText = this.state.isLinkCopied ? this.dialogStrings.copiedButton : this.dialogStrings.copyButton;
        const ariaLabel = this.state.isLinkCopied ? this.dialogStrings.copiedButtonAriaLabel : this.dialogStrings.copyButtonAriaLabel;

        const copyTelemetryTag = copyButtonTelemetryTags || copyButtonTelemetryObject.render();

        return (
            <div className={managedClasses.inputContainer}>
                <input
                    ref={this.inputRef}
                    className={managedClasses.copyLinkInput}
                    aria-label={ariaLabel}
                    tabIndex={-1}
                    type="text"
                    value={url}
                    readOnly
                />
                <AccentButton
                    {...copyButtonAttributes}
                    {...copyTelemetryTag}
                    className={managedClasses.copyLinkButton}
                    onClick={this.onCopyLinkToClipboard}
                    title={ariaLabel}
                    aria-label={ariaLabel}>
                    {buttonText}
                </AccentButton>
            </div>
        );
    }

    /**
     * Copy share url to clipboard and set timer to update the copy link button text
     * @private
     * @method
     */
    private onCopyLinkToClipboard = () => {
        if (!this.state.isLinkCopied) {
            this.setState({ isLinkCopied: true });
            this.inputRef.current.select();
            document.execCommand("copy");
            setTimeout(() => {
                /* istanbul ignore next */
                this.setState({ isLinkCopied: false });
            },
            linkCopiedDisplayDuration
            );
        }
    };

    /**
     * Renders the social items list part of the share dialog
     * @private
     * @method
     * @returns {JSX.Element} the social media list element of the ShareDialog component
     */
    private renderSocialIconsList(): JSX.Element {
        const { managedClasses, socialMediaItems } = this.props;
        if (!socialMediaItems || !socialMediaItems.length) {
            return null;
        }

        const socialMediaIcons = socialMediaItems.map((item) => {
            return this.renderSocialIcon(item);
        });

        return (
            <ul className={managedClasses.iconListContainer}>
                {socialMediaIcons}
            </ul>
        );
    }

    /**
     * Renders each individual social media icon of the social media list
     * @private
     * @method
     * @param { string } socialMediaItem The social media item as string
     * @returns {JSX.Element} the icon part of the social media list
     */
    private renderSocialIcon(socialMediaItem: string): JSX.Element {
        const { managedClasses, shareIconTelemetryObject, shareIconTelemetryTags, contentTitle, contentUrl, emailBody, requestContext } = this.props;
        const media: SocialMedia = SocialMedia[Object.keys(SocialMedia).find(key => SocialMedia[key] === socialMediaItem.toLowerCase())];
        if (!media) {
            logger.logError(`ShareDialog: renderSocialIcon: Invalid param: socialMediaItem = ${socialMediaItem}`);
            return null;
        }
        const mediaKey = media.toString();

        const iconTelemetryTags: TelemetryTags = (shareIconTelemetryTags && shareIconTelemetryTags[mediaKey]) ||
            (shareIconTelemetryObject && shareIconTelemetryObject[mediaKey].render());

        return (
            <li key={mediaKey} className={managedClasses.iconListItem} >
                <SocialMediaIcon
                    socialMedia={ media }
                    contentTitle={ contentTitle }
                    contentUrl={ contentUrl }
                    emailBody={ emailBody }
                    socialMediaItemConfigOverride={this.socialMediaItemsConfig[mediaKey]}
                    requestContext={ requestContext }
                    {...iconTelemetryTags}
                />
            </li>
        );
    }

    /**
     * Sets configuration specs for social media items based on default and override props values
     * @method
     */
    private setSocialMediaItemsConfig(): void {
        this.socialMediaItemsConfig = { ...SocialMediaDefaultConfig };

        // merge default config with override props
        if (this.props.socialMediaConfigOverride) {
            Object.keys(this.props.socialMediaConfigOverride).forEach((key) => {
                const keyLower = key.toLowerCase();
                this.socialMediaItemsConfig[keyLower] = { ...this.socialMediaItemsConfig[keyLower], ...this.props.socialMediaConfigOverride[key] };
            });
        }
    }

    /**
     * Gets the final props to apply to the dialog component
     * @private
     * @method
     * @param {DialogProps} props - the default dialog props
     * @returns {DialogProps} the resulting props to be passed to base dialog
     */
    private coerceDialogProps(props: DialogProps): DialogProps {
        return {
            visible: this.props.enabled,
            modal: true,
            contentHeight: "auto",
            contentWidth: this.props.renderOneColLayout ? "100%" : "346px",
            ...props
        };
    }
}
